import React, { useEffect, useState } from 'react';
import { Mail, Phone, Smartphone } from 'lucide-react';
import { telHref } from '../utils/telefon';
import Combobox from './ui/Combobox';
import Modal from './ui/Modal';
import apiService from '../services/apiService';
import { getCustomerOptions, invalidateCustomerCache } from '../utils/customerCache';
import { getCustomerContacts, invalidateContactCache } from '../utils/contactCache';
import { getFeatures } from '../utils/featuresCache';
import { useToast } from './ToastProvider';
import { useI18n } from '../utils/i18n';

// Kunden-Auswahl für die Ticket-Dialoge (Anlegen/Bearbeiten, #89/#86).
// Rendert nichts, wenn das Kunden-Feature (app_settings) deaktiviert ist.
// Admins/Manager bekommen zusätzlich "+ Neuen Kunden anlegen" (#88) —
// die Anlage läuft über die bestehende Admin-Route (Server prüft die Rolle).
//
// #105: Optional mit Ansprechpartner-Feld daneben — aktiv, sobald der
// Aufrufer `onContactChange` übergibt. Das Dropdown zeigt nur die
// Ansprechpartner des gewählten Kunden (vorher leer/gesperrt), ein
// Kundenwechsel setzt den Kontakt zurück. Die Nummern des gewählten
// Ansprechpartners stehen rechts darunter als tel:-Links (Klick ruft an,
// z. B. über Teams; Rechtsklick kopiert die Nummer). Inline-Neuanlage wie
// beim Kunden über die Admin-Route.
//
// #551: Der gewählte Ansprechpartner lässt sich hier auch bearbeiten (Name,
// Mobil, Festnetz, E-Mail), und die Neuanlage fragt die E-Mail gleich mit ab.
// Beides nur für Manager und Admins (Beschluss 22.10.2026), über dieselben
// Admin-Routen wie die Kundenverwaltung. Notizen und Aktiv bleiben dort.

// Rolle einmal pro Seitenaufruf ermitteln; /api/user ist leichtgewichtig,
// aber ein Aufruf je Modal-Mount wäre trotzdem unnötig.
let canCreatePromise = null;
function canCreateCustomers() {
  if (!canCreatePromise) {
    canCreatePromise = apiService.getUser()
      .then(u => !!(u?.dbUser?.isAdmin || u?.dbUser?.isManager))
      .catch(() => false);
  }
  return canCreatePromise;
}

// telHref liegt seit #255 in utils/telefon (die Kontaktsuche braucht ihn auch);
// der Re-Export hält bestehende Importe (TicketDetail) gültig.
export { telHref };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TicketCustomerField = ({
  value,
  onChange,
  inputId = 'ticket-customer',
  className = '',
  disabled = false,
  contactValue = null,
  onContactChange = null,
  contactInputId = 'ticket-contact'
}) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const hasContactField = typeof onContactChange === 'function';
  const [enabled, setEnabled] = useState(false);
  const [options, setOptions] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createShortName, setCreateShortName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  // #105: Ansprechpartner; #551: ein Formular für Anlegen und Bearbeiten
  const [contacts, setContacts] = useState([]);
  const [contactForm, setContactForm] = useState(null); // null | { id: null|Zahl, name, phoneMobile, phoneLandline, email }
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getFeatures().then(features => {
      if (cancelled || features.customers === false) return;
      setEnabled(true);
      getCustomerOptions().then(opts => { if (!cancelled) setOptions(opts); });
      canCreateCustomers().then(ok => { if (!cancelled) setCanCreate(ok); });
    });
    return () => { cancelled = true; };
  }, []);

  // Kontakte des gewählten Kunden laden (nur wenn das Feld aktiv ist)
  useEffect(() => {
    if (!hasContactField || !enabled) return undefined;
    let cancelled = false;
    if (!value) {
      setContacts([]);
      return undefined;
    }
    getCustomerContacts(value).then(list => { if (!cancelled) setContacts(list); });
    return () => { cancelled = true; };
  }, [hasContactField, enabled, value]);

  if (!enabled) return null;

  const handleCustomerChange = (customerId) => {
    onChange(customerId);
    // Kundenwechsel: der alte Ansprechpartner gehört nicht zum neuen Kunden
    if (hasContactField && customerId !== value) onContactChange(null);
  };

  const openCreate = () => {
    setCreateName('');
    setCreateShortName('');
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createName.trim() || createBusy) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const resp = await apiService.createCustomer({
        name: createName.trim(),
        shortName: createShortName.trim() || null
      });
      invalidateCustomerCache();
      setOptions(await getCustomerOptions());
      setCreateOpen(false);
      if (resp?.customer?.id) handleCustomerChange(resp.customer.id);
    } catch (e) {
      console.error('Create customer from ticket dialog error:', e);
      setCreateError(e.response?.data?.error || t('customerField.createError', 'Kunde konnte nicht angelegt werden.'));
    } finally {
      setCreateBusy(false);
    }
  };

  // ----- #105: Ansprechpartner -----
  const selectedContact = contacts.find(k => k.id === contactValue) || null;
  const contactOptions = contacts
    .filter(k => k.isActive !== false || k.id === contactValue)
    .map(k => ({ value: k.id, label: k.name }));

  const copyNumber = (e, num) => {
    e.preventDefault();
    try {
      navigator.clipboard.writeText(num);
      pushToast({ message: t('customerField.numberCopied', 'Nummer kopiert.'), type: 'success' });
    } catch (_) {}
  };

  const openContactForm = (contact = null) => {
    setContactForm({
      id: contact ? contact.id : null,
      name: contact?.name || '',
      phoneMobile: contact?.phoneMobile || '',
      phoneLandline: contact?.phoneLandline || '',
      email: contact?.email || ''
    });
    setContactError(null);
  };
  const setContactFeld = (feld, wert) => setContactForm(prev => ({ ...prev, [feld]: wert }));
  const contactEdit = !!(contactForm && contactForm.id);

  const handleContactSave = async () => {
    if (!contactForm || !contactForm.name.trim() || contactBusy || !value) return;
    const email = contactForm.email.trim();
    if (email && !EMAIL_RE.test(email)) {
      setContactError(t('customerField.contactEmailInvalid', 'Bitte eine gültige E-Mail-Adresse eingeben.'));
      return;
    }
    setContactBusy(true);
    setContactError(null);
    const payload = {
      name: contactForm.name.trim(),
      phoneMobile: contactForm.phoneMobile.trim() || null,
      phoneLandline: contactForm.phoneLandline.trim() || null,
      email: email || null
    };
    try {
      const resp = contactEdit
        ? await apiService.updateCustomerContact(contactForm.id, payload)
        : await apiService.createCustomerContact(value, payload);
      invalidateContactCache(value);
      setContacts(await getCustomerContacts(value));
      setContactForm(null);
      if (contactEdit) {
        pushToast({ message: t('admin.contacts.saved', 'Ansprechpartner gespeichert.'), type: 'success' });
      } else if (resp?.contact?.id) {
        onContactChange(resp.contact.id);
      }
    } catch (e) {
      console.error('Save customer contact from ticket dialog error:', e);
      setContactError(e.response?.data?.error || (contactEdit
        ? t('admin.contacts.saveError', 'Fehler beim Speichern des Ansprechpartners.')
        : t('customerField.createContactError', 'Ansprechpartner konnte nicht angelegt werden.')));
    } finally {
      setContactBusy(false);
    }
  };

  const customerGroup = (
    <div className={hasContactField || !className ? 'form-group' : `form-group ${className}`}>
      <label htmlFor={inputId}>{t('ticketDetail.customer', 'Kunde')}</label>
      <Combobox
        inputId={inputId}
        options={options}
        value={value || null}
        onChange={handleCustomerChange}
        disabled={disabled}
        allowClear
        clearLabel={t('ticketDetail.customerNone', '— kein Kunde —')}
        noResultsText={t('ui.noResults', 'Keine Treffer')}
        placeholder={t('ticketDetail.customerSelect', 'Kunde wählen…')}
      />
      {canCreate && !disabled && (
        <button
          type="button"
          className="customer-field-create-link"
          onClick={openCreate}
        >
          + {t('customerField.createNew', 'Neuen Kunden anlegen')}
        </button>
      )}
      <Modal
        open={createOpen}
        onClose={() => { if (!createBusy) setCreateOpen(false); }}
        title={t('customerField.createTitle', 'Neuen Kunden anlegen')}
        maxWidth="420px"
        closeLabel={t('common.close', 'Schließen')}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(false)} disabled={createBusy}>
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={createBusy || !createName.trim()}>
              {t('customerField.createConfirm', 'Anlegen')}
            </button>
          </>
        )}
      >
        {createError && <div className="alert alert-error">{createError}</div>}
        <div className="form-group">
          <label htmlFor="customer-create-name">{t('admin.customers.name', 'Kundenname')} <span className="required">*</span></label>
          <input
            id="customer-create-name"
            type="text"
            className="form-input"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="form-group">
          <label htmlFor="customer-create-shortname">{t('admin.customers.shortName', 'Kurzform (Anzeige in Tickets)')}</label>
          <input
            id="customer-create-shortname"
            type="text"
            className="form-input"
            value={createShortName}
            onChange={(e) => setCreateShortName(e.target.value)}
          />
        </div>
        <p className="section-description">
          {t('customerField.createHint', 'Domänen, Telefonnummern und weitere Daten können später in der Kundenverwaltung ergänzt werden.')}
        </p>
      </Modal>
    </div>
  );

  if (!hasContactField) return customerGroup;

  return (
    <div className="customer-contact-row">
      {customerGroup}
      <div className="form-group">
        <label htmlFor={contactInputId}>{t('customerField.contact', 'Ansprechpartner')}</label>
        <Combobox
          inputId={contactInputId}
          options={contactOptions}
          value={contactValue || null}
          onChange={onContactChange}
          disabled={disabled || !value}
          allowClear
          clearLabel={t('customerField.contactNone', '— kein Ansprechpartner —')}
          noResultsText={t('ui.noResults', 'Keine Treffer')}
          placeholder={value
            ? t('customerField.contactSelect', 'Ansprechpartner wählen…')
            : t('customerField.contactNoCustomer', 'Erst Kunde wählen')}
        />
        {selectedContact && (selectedContact.phoneMobile || selectedContact.phoneLandline || selectedContact.email) && (
          <div className="contact-phone-links">
            {selectedContact.phoneMobile && (
              <a
                href={telHref(selectedContact.phoneMobile)}
                title={t('customerField.callTitle', 'Anrufen — Rechtsklick kopiert die Nummer')}
                onContextMenu={(e) => copyNumber(e, selectedContact.phoneMobile)}
              >
                <Smartphone size={13} />
                {selectedContact.phoneMobile}
              </a>
            )}
            {selectedContact.phoneLandline && (
              <a
                href={telHref(selectedContact.phoneLandline)}
                title={t('customerField.callTitle', 'Anrufen — Rechtsklick kopiert die Nummer')}
                onContextMenu={(e) => copyNumber(e, selectedContact.phoneLandline)}
              >
                <Phone size={13} />
                {selectedContact.phoneLandline}
              </a>
            )}
            {selectedContact.email && (
              <a href={`mailto:${selectedContact.email}`}>
                <Mail size={13} />
                {selectedContact.email}
              </a>
            )}
          </div>
        )}
        {canCreate && !disabled && value && (
          <div className="customer-field-links">
            {selectedContact && (
              <button
                type="button"
                className="customer-field-create-link"
                onClick={() => openContactForm(selectedContact)}
                title={t('customerField.editContact', 'Ansprechpartner bearbeiten')}
                aria-label={t('customerField.editContact', 'Ansprechpartner bearbeiten')}
              >
                {t('ticketDetail.edit', 'Bearbeiten')}
              </button>
            )}
            <button
              type="button"
              className="customer-field-create-link"
              onClick={() => openContactForm()}
            >
              + {t('customerField.createNewContact', 'Neuen Ansprechpartner anlegen')}
            </button>
          </div>
        )}
        <Modal
          open={!!contactForm}
          onClose={() => { if (!contactBusy) setContactForm(null); }}
          title={contactEdit
            ? t('customerField.editContactTitle', 'Ansprechpartner bearbeiten')
            : t('customerField.createContactTitle', 'Neuen Ansprechpartner anlegen')}
          maxWidth="420px"
          closeLabel={t('common.close', 'Schließen')}
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setContactForm(null)} disabled={contactBusy}>
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleContactSave} disabled={contactBusy || !contactForm?.name.trim()}>
                {contactEdit ? t('admin.contacts.save', 'Speichern') : t('customerField.createConfirm', 'Anlegen')}
              </button>
            </>
          )}
        >
          {contactForm && (
            <>
              {contactError && <div className="alert alert-error">{contactError}</div>}
              <div className="form-group">
                <label htmlFor="contact-create-name">{t('admin.contacts.name', 'Name')} <span className="required">*</span></label>
                <input
                  id="contact-create-name"
                  type="text"
                  className="form-input"
                  value={contactForm.name}
                  onChange={(e) => setContactFeld('name', e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-create-mobile">{t('admin.contacts.phoneMobile', 'Mobil')}</label>
                <input
                  id="contact-create-mobile"
                  type="text"
                  className="form-input"
                  value={contactForm.phoneMobile}
                  onChange={(e) => setContactFeld('phoneMobile', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-create-landline">{t('admin.contacts.phoneLandline', 'Festnetz')}</label>
                <input
                  id="contact-create-landline"
                  type="text"
                  className="form-input"
                  value={contactForm.phoneLandline}
                  onChange={(e) => setContactFeld('phoneLandline', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-create-email">{t('admin.contacts.email', 'E-Mail')}</label>
                <input
                  id="contact-create-email"
                  type="email"
                  className="form-input"
                  value={contactForm.email}
                  onChange={(e) => setContactFeld('email', e.target.value)}
                />
              </div>
              <p className="section-description">
                {contactEdit
                  ? t('customerField.editContactHint', 'Die Änderung gilt für alle Tickets dieses Ansprechpartners. Notizen stehen in der Kundenverwaltung.')
                  : t('customerField.createContactHint', 'Notizen können später in der Kundenverwaltung ergänzt werden.')}
              </p>
            </>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default TicketCustomerField;
